import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@staff-app/ui';
import type { TestingDeviceCommands } from '../../hooks/useTestingDeviceCommands';
import { errorText } from './lib/device-commands';
import { parseDeviceLiveInfo, type DeviceLiveSettings } from './lib/parse-device-info';
import {
  buildDisplayPatch,
  buildLockdownPasswordPatch,
  buildPlaybackPatch,
  buildStoragePatch,
  displayBaseline,
  ORIENTATION_OPTIONS,
  playbackBaseline,
  SCALE_OPTIONS,
  storageBaseline,
  supportsExternalStorage,
  supportsOpsSchedule,
  type DisplayValues,
  type PlaybackValues,
  type SettingsPayload,
  type StorageValues,
} from './lib/settings-payload';
import type { TestingDeviceDetail } from './lib/types';
import { OpsScheduleField } from './OpsScheduleField';

type Section = 'display' | 'ops' | 'lockdown' | 'playback' | 'remote' | 'storage';
type FeatureToggle = 'LOCKDOWN' | 'PLAYBACK_CONTROL' | 'REMOTE_CONTROL';

const TOGGLE_FEATURE_KEY: Record<FeatureToggle, string> = {
  LOCKDOWN: 'lockdown',
  PLAYBACK_CONTROL: 'playbackControl',
  REMOTE_CONTROL: 'remoteControl',
};
const TOGGLE_SECTION: Record<FeatureToggle, Section> = {
  LOCKDOWN: 'lockdown',
  PLAYBACK_CONTROL: 'playback',
  REMOTE_CONTROL: 'remote',
};

interface Props {
  detail: TestingDeviceDetail;
  /** This open's live GET_DEVICE_INFO settings readback, when the device answered. */
  liveSettings?: DeviceLiveSettings;
  commands: TestingDeviceCommands;
  /** After any save: refetch detail, activity feed, staff actions, command history. */
  onSaved: () => void;
}

function Group({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-3 shadow-sm" data-testid={testId}>
      <div className="mb-3 text-xs font-semibold text-muted-foreground">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/** Info icon + hover tooltip with an optional help link — scio3 `InfoHint`. */
function InfoHint({ text, helpUrl }: { text: string; helpUrl?: string }) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info
            className="h-3.5 w-3.5 shrink-0 cursor-help text-muted-foreground"
            aria-label={text}
          />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{text}</p>
          {helpUrl ? (
            <a
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block text-xs text-primary hover:underline"
            >
              {'More info here.'}
            </a>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ToggleRow({
  id,
  label,
  checked,
  onChange,
  disabled,
  tooltip,
  tooltipLink,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  tooltip?: string;
  tooltipLink?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      {/* The hint sits beside the Label (not inside it) so clicking it never toggles the switch. */}
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id} className="text-sm font-normal">
          {label}
        </Label>
        {tooltip ? <InfoHint text={tooltip} helpUrl={tooltipLink} /> : null}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        aria-label={label}
        disabled={disabled}
        data-testid={id}
      />
    </div>
  );
}

function SliderRow({
  id,
  label,
  value,
  onChange,
  onCommit,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
  onCommit: (v: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-sm font-normal">
          {label}
        </Label>
        <span className="text-xs text-muted-foreground" data-testid={`${id}-value`}>
          {`${value}%`}
        </span>
      </div>
      <Slider
        id={id}
        aria-label={label}
        min={0}
        max={100}
        step={1}
        value={value}
        onValueChange={onChange}
        onValueCommit={onCommit}
        data-testid={id}
      />
    </div>
  );
}

function SectionError({ section, message }: { section: Section; message?: string | null }) {
  if (!message) return null;
  return (
    <p className="text-xs text-destructive" data-testid={`testing-settings-${section}-error`}>
      {message}
    </p>
  );
}

/**
 * My Testing Devices › Settings — port of scio3 `DeviceSettingsTab` (+ `DeviceOpsScheduleField`) over
 * the staff-api testing proxies. Saves ON CHANGE exactly like scio3 — switches / selects / checkboxes
 * the moment they change, sliders on release (`onValueCommit`), text fields on blur — with no Save
 * buttons. Kept from the staff-side safety rules:
 *  - each save sends ONLY the key that changed, in the shape the staff-api allowlist accepts;
 *  - a failed save shows the server's message on the section (and a toast) and rolls the control back;
 *  - the unlock password is write-only (never seeded from the record, cleared after a save);
 *  - named Ops schedules can be PICKED, never created or edited here (the ✏️ points at the portal).
 */
export function SettingsTab({ detail, liveSettings, commands, onSaved }: Props) {
  const cap = detail.classification?.managementCapability ?? null;
  const feature = detail.feature ?? {};
  const reported = liveSettings ?? parseDeviceLiveInfo(detail.mdmReported?.data ?? null)?.settings;

  const [errors, setErrors] = useState<Partial<Record<Section, string | null>>>({});
  const [pendingToggle, setPendingToggle] = useState<FeatureToggle | null>(null);

  // Local overrides on top of the record: a control shows its new value immediately (optimistic) and
  // the override is reverted when the save fails. Sliders also move through here while dragging.
  const [displayOver, setDisplayOver] = useState<Partial<DisplayValues>>({});
  const display = { ...displayBaseline(detail, reported), ...displayOver };
  const [playbackOver, setPlaybackOver] = useState<Partial<PlaybackValues>>({});
  const playback = { ...playbackBaseline(detail), ...playbackOver };
  const [storageOver, setStorageOver] = useState<Partial<StorageValues>>({});
  const storage = { ...storageBaseline(detail), ...storageOver };
  const [toggleOver, setToggleOver] = useState<Partial<Record<FeatureToggle, boolean>>>({});
  // Text inputs hold their draft until blur.
  const [timeoutDraft, setTimeoutDraft] = useState<string | null>(null);
  const [pathDraft, setPathDraft] = useState<string | null>(null);
  // Write-only: never seeded from `feature.lockdownPassword`.
  const [password, setPassword] = useState('');

  const setError = (section: Section, message: string | null) =>
    setErrors((prev) => ({ ...prev, [section]: message }));

  /** Persist one change; resolves true on success. `rollback` runs on failure. */
  const save = async (
    section: Section,
    payload: SettingsPayload | null,
    rollback?: () => void,
  ): Promise<boolean> => {
    if (!payload) return false;
    setError(section, null);
    try {
      await commands.updateDevice(payload);
      toast.success('Saved');
      onSaved();
      return true;
    } catch (err) {
      const message = errorText(err, 'Failed to save');
      setError(section, message);
      toast.error(message);
      rollback?.();
      return false;
    }
  };

  const setDisplay = <K extends keyof DisplayValues>(key: K, next: DisplayValues[K]) => {
    const prev = display[key];
    const patch = buildDisplayPatch(display, { [key]: next } as Partial<DisplayValues>);
    setDisplayOver((d) => ({ ...d, [key]: next }));
    void save('display', patch, () => setDisplayOver((d) => ({ ...d, [key]: prev })));
  };

  /** Slider release: `from` is the value when the drag started (the last saved one). */
  const commitDisplaySlider = (key: 'brightness' | 'volume', from: number, next: number) => {
    const patch = buildDisplayPatch({ ...display, [key]: from }, { [key]: next });
    void save('display', patch, () => setDisplayOver((d) => ({ ...d, [key]: from })));
  };
  // Value at drag start, per slider — what a failed commit rolls back to.
  const [dragFrom, setDragFrom] = useState<Partial<Record<'brightness' | 'volume', number>>>({});
  const dragSlider = (key: 'brightness' | 'volume', v: number) => {
    setDragFrom((f) => (f[key] === undefined ? { ...f, [key]: display[key] } : f));
    setDisplayOver((d) => ({ ...d, [key]: v }));
  };
  const releaseSlider = (key: 'brightness' | 'volume', v: number) => {
    const from = dragFrom[key] ?? display[key];
    setDragFrom((f) => ({ ...f, [key]: undefined }));
    setDisplayOver((d) => ({ ...d, [key]: v }));
    commitDisplaySlider(key, from, v);
  };

  const setPlayback = <K extends keyof PlaybackValues>(key: K, next: PlaybackValues[K]) => {
    const prev = playback[key];
    const patch = buildPlaybackPatch(playback, { [key]: next } as Partial<PlaybackValues>);
    if (!patch) return;
    setPlaybackOver((d) => ({ ...d, [key]: next }));
    void save('playback', patch, () => setPlaybackOver((d) => ({ ...d, [key]: prev })));
  };

  const setStorage = <K extends keyof StorageValues>(key: K, next: StorageValues[K]) => {
    const prev = storage[key];
    const patch = buildStoragePatch(storage, { [key]: next } as Partial<StorageValues>);
    if (!patch) return;
    setStorageOver((d) => ({ ...d, [key]: next }));
    void save('storage', patch, () => setStorageOver((d) => ({ ...d, [key]: prev })));
  };

  const toggleOn = (key: FeatureToggle) =>
    toggleOver[key] ?? Boolean(feature[TOGGLE_FEATURE_KEY[key]]);

  const toggle = async (key: FeatureToggle, next: boolean) => {
    const prev = toggleOn(key);
    const section = TOGGLE_SECTION[key];
    setToggleOver((t) => ({ ...t, [key]: next }));
    setPendingToggle(key);
    setError(section, null);
    try {
      await commands.toggleFeature(key, next);
      toast.success('Saved');
      onSaved();
    } catch (err) {
      const message = errorText(err, 'Failed to save');
      setError(section, message);
      toast.error(message);
      setToggleOver((t) => ({ ...t, [key]: prev }));
    } finally {
      setPendingToggle(null);
    }
  };

  const savePassword = async () => {
    const patch = buildLockdownPasswordPatch(password);
    if (!patch) return;
    if (await save('lockdown', patch)) setPassword('');
  };

  const commitTimeout = () => {
    if (timeoutDraft === null) return;
    const n = Math.max(1, parseInt(timeoutDraft, 10) || 1);
    setTimeoutDraft(null);
    setPlayback('timeout', n);
  };

  const commitPath = () => {
    if (pathDraft === null) return;
    const next = pathDraft;
    setPathDraft(null);
    setStorage('path', next);
  };

  const lockdownOn = toggleOn('LOCKDOWN');
  const playbackOn = toggleOn('PLAYBACK_CONTROL');

  return (
    <div className="space-y-4" data-testid="testing-settings">
      <Group title={'Display'} testId="testing-settings-display">
        <ToggleRow
          id="testing-set-muted"
          label={'Mute'}
          checked={display.muted}
          onChange={(v) => setDisplay('muted', v)}
        />
        <SliderRow
          id="testing-set-brightness"
          label={'Brightness'}
          value={display.brightness}
          onChange={(v) => dragSlider('brightness', v)}
          onCommit={(v) => releaseSlider('brightness', v)}
        />
        <SliderRow
          id="testing-set-volume"
          label={'Volume'}
          value={display.volume}
          onChange={(v) => dragSlider('volume', v)}
          onCommit={(v) => releaseSlider('volume', v)}
        />
        <div className="space-y-2">
          <Label htmlFor="testing-set-orientation" className="text-sm font-normal">
            {'Orientation'}
          </Label>
          <Select
            value={display.orientation || undefined}
            onValueChange={(v) => setDisplay('orientation', v)}
          >
            <SelectTrigger id="testing-set-orientation" className="w-44 cursor-pointer">
              <SelectValue placeholder={'Orientation'} />
            </SelectTrigger>
            <SelectContent>
              {ORIENTATION_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="testing-set-scale" className="text-sm font-normal">
            {'Scale'}
          </Label>
          <Select value={display.scale || undefined} onValueChange={(v) => setDisplay('scale', v)}>
            <SelectTrigger id="testing-set-scale" className="w-44 cursor-pointer">
              <SelectValue placeholder={'Scale'} />
            </SelectTrigger>
            <SelectContent>
              {SCALE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionError section="display" message={errors.display} />
        <p className="text-xs text-muted-foreground">
          {reported
            ? 'Mute, volume and brightness show the values reported by the device.'
            : 'Brightness, volume and mute apply on the next device sync.'}
        </p>
      </Group>

      {supportsOpsSchedule(detail) ? (
        <Group title={'Operational Schedule'} testId="testing-settings-ops">
          <OpsScheduleField
            deviceId={detail._id}
            feature={feature}
            onSaveFeature={(patch, rollback) => save('ops', patch, rollback)}
          />
          <SectionError section="ops" message={errors.ops} />
          <p className="text-xs text-muted-foreground">
            {'Schedule TV power, volume, brightness and mute by time of day.'}
          </p>
        </Group>
      ) : null}

      <Group title={'Controls'} testId="testing-settings-controls">
        <div className="space-y-2">
          <ToggleRow
            id="testing-set-lockdown"
            label={'Lockdown'}
            checked={lockdownOn}
            onChange={(v) => void toggle('LOCKDOWN', v)}
            disabled={pendingToggle === 'LOCKDOWN'}
            tooltip={
              'Hide OptiSigns controls, menu to prevent viewers from making changes to the app. If running Kiosk, viewers still can interact with Kiosk, but just cannot use the menu or close app.'
            }
            tooltipLink="https://support.optisigns.com/hc/en-us/articles/30310366838803-Enabling-Lock-Down-Mode-on-Touch-Screen-Devices"
          />
          {lockdownOn ? (
            <div className="space-y-1 pl-1">
              <Label htmlFor="testing-set-lockdown-password" className="text-sm font-normal">
                {'Unlock password'}
              </Label>
              <Input
                id="testing-set-lockdown-password"
                type="password"
                autoComplete="new-password"
                maxLength={10}
                placeholder={
                  feature.lockdownPassword
                    ? 'Set a new unlock password'
                    : 'Set unlock password (max 10 chars)'
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => void savePassword()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void savePassword();
                }}
              />
              <p
                className={cn(
                  'text-xs',
                  feature.lockdownPassword ? 'text-muted-foreground' : 'text-warning',
                )}
              >
                {feature.lockdownPassword
                  ? 'An unlock password is set. It is never shown here.'
                  : 'Set an unlock password — without one the screen cannot be unlocked.'}
              </p>
            </div>
          ) : null}
          <SectionError section="lockdown" message={errors.lockdown} />
        </div>

        <div className="space-y-2">
          <ToggleRow
            id="testing-set-playback"
            label={'Playback control'}
            checked={playbackOn}
            onChange={(v) => void toggle('PLAYBACK_CONTROL', v)}
            disabled={pendingToggle === 'PLAYBACK_CONTROL'}
            tooltip={'Enable viewer to navigate forward, backward, pause the playlist.'}
            tooltipLink="https://links.optisigns.com/dxAy"
          />
          {playbackOn ? (
            <div className="space-y-3 pl-1">
              <div className="space-y-1">
                <Label htmlFor="testing-set-playback-timeout" className="text-sm font-normal">
                  {'Timeout in seconds'}
                </Label>
                <Input
                  id="testing-set-playback-timeout"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={timeoutDraft ?? String(playback.timeout)}
                  onChange={(e) => setTimeoutDraft(e.target.value)}
                  onBlur={commitTimeout}
                  className="w-24"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={playback.autoplay}
                  onCheckedChange={(c) => setPlayback('autoplay', c === true)}
                  data-testid="testing-set-playback-autoplay"
                />
                {'Autoplay Video'}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={playback.navBar}
                  onCheckedChange={(c) => setPlayback('navBar', c === true)}
                  data-testid="testing-set-playback-navbar"
                />
                {'Show NavBar'}
              </label>
            </div>
          ) : null}
          <SectionError section="playback" message={errors.playback} />
        </div>

        <div className="space-y-2">
          <ToggleRow
            id="testing-set-remote"
            label={'Remote control'}
            checked={toggleOn('REMOTE_CONTROL')}
            onChange={(v) => void toggle('REMOTE_CONTROL', v)}
            disabled={pendingToggle === 'REMOTE_CONTROL'}
            tooltip={
              "Remotely view what's on your screen, you can also control & troubleshoot your device."
            }
            tooltipLink="https://links.optisigns.com/15F5"
          />
          <SectionError section="remote" message={errors.remote} />
        </div>
      </Group>

      {supportsExternalStorage(cap) ? (
        <Group title={'Storage'} testId="testing-settings-storage">
          <ToggleRow
            id="testing-set-external-storage"
            label={'External storage'}
            checked={storage.enabled}
            onChange={(v) => setStorage('enabled', v)}
            tooltip={'Save and play content from a USB drive or SD card plugged into the device.'}
            tooltipLink="https://support.optisigns.com/hc/en-us/articles/20153471319187-External-Storage-on-OptiSigns-Devices"
          />
          {storage.enabled ? (
            <div className="space-y-1 pl-1">
              <Label htmlFor="testing-set-storage-path" className="text-sm font-normal">
                {'Storage Path'}
              </Label>
              <Input
                id="testing-set-storage-path"
                placeholder={'Leave empty to auto-detect'}
                value={pathDraft ?? storage.path}
                onChange={(e) => setPathDraft(e.target.value)}
                onBlur={commitPath}
              />
            </div>
          ) : null}
          <SectionError section="storage" message={errors.storage} />
        </Group>
      ) : null}

      <p className="px-1 text-xs text-muted-foreground">
        {'Time zone, Wi-Fi and OTA are in the action bar (More).'}
      </p>
    </div>
  );
}
